import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  AlertTriangle,
  CheckCircle2,
  Database,
  Loader2,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const modHeaders = () => ({
  Authorization: `Bearer ${sessionStorage.getItem("mod_token")}`,
});

const TYPE_LABELS = {
  facebook_page: "Facebook-sida",
  facebook_group_hint: "Facebook-grupp (manuell)",
  facebook_manual: "Facebook-hänvisning",
  source_watch: "Källbevakning",
  html_stromsund_events: "Kommunal evenemangsimport",
  api_pro_bodum: "PRO-aktiviteter",
};

const MONITORING_LABELS = {
  automated: "automatisk bevakning",
  manual_review: "manuell granskning",
};

function isMaster(user) {
  return user && (user.role === "master" || user.role === "admin");
}

export default function RegionalSourcePackPanel({ onImported }) {
  const user = JSON.parse(sessionStorage.getItem("mod_user") || "null");
  const token = sessionStorage.getItem("mod_token");
  const canEdit = Boolean(token && isMaster(user));

  const [pack, setPack] = useState(null);
  const [selected, setSelected] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");

  const loadPack = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.get(`${API}/mod/event-sources/regional-pack`, {
        headers: modHeaders(),
      });
      setPack(data);
      setSelected((data.items || []).filter((item) => item.status === "missing").map((item) => item.id));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Källpaketet kunde inte läsas.");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadPack();
  }, [loadPack]);

  const missingItems = useMemo(
    () => (pack?.items || []).filter((item) => item.status === "missing"),
    [pack],
  );

  if (!canEdit) return null;

  const toggle = (sourceId) => {
    setSelected((current) => (
      current.includes(sourceId)
        ? current.filter((value) => value !== sourceId)
        : [...current, sourceId]
    ));
  };

  const importSelected = async () => {
    if (!selected.length) return;
    setImporting(true);
    setError("");
    try {
      const { data } = await axios.post(
        `${API}/mod/event-sources/regional-pack/import`,
        { source_ids: selected },
        { headers: modHeaders() },
      );
      toast.success("Regionala källor behandlade", {
        description: `${data.created_count} tillagda · ${data.skipped_count} redan befintliga · ${data.failed_count} fel`,
      });
      setPack(data.status);
      setSelected((data.status?.items || []).filter((item) => item.status === "missing").map((item) => item.id));
      onImported?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Källorna kunde inte importeras.");
    } finally {
      setImporting(false);
    }
  };

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-5" data-testid="regional-source-pack">
      <div className="bg-white border border-warm-border rounded-sm shadow-sm p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <Database size={20} className="text-pine shrink-0 mt-0.5" />
            <div>
              <h2 className="font-heading text-lg font-medium text-ink">Regionalt källpaket</h2>
              <p className="text-sm text-bark mt-1 max-w-3xl">
                Förberedda verifierade Facebook-källor för VIDÄLV. Alla registreras i aktiv bevakningslista.
                Offentliga sidor kan bevakas automatiskt; Facebook-grupper kräver manuell granskning och hämtas inte automatiskt.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={loadPack}
            disabled={loading || importing}
            className="inline-flex items-center gap-2 border border-warm-border px-3 py-2 text-sm text-ink hover:bg-sand/40 disabled:opacity-50"
          >
            {loading ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
            Uppdatera
          </button>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 text-sm text-falu">
            <AlertTriangle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {loading && !pack ? (
          <div className="py-6 flex items-center gap-2 text-sm text-bark">
            <Loader2 size={16} className="animate-spin" /> Läser källpaketet…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-4 text-sm">
              <div className="border border-warm-border p-3">
                <div className="text-xs text-muted-foreground">Totalt</div>
                <div className="text-xl font-semibold text-ink">{pack?.total_count || 0}</div>
              </div>
              <div className="border border-pine/30 bg-pine/5 p-3">
                <div className="text-xs text-muted-foreground">Saknas</div>
                <div className="text-xl font-semibold text-pine">{pack?.missing_count || 0}</div>
              </div>
              <div className="border border-warm-border bg-sand/20 p-3">
                <div className="text-xs text-muted-foreground">Finns redan</div>
                <div className="text-xl font-semibold text-ink">{pack?.existing_count || 0}</div>
              </div>
            </div>

            <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
              {(pack?.items || []).map((item) => {
                const exists = item.status === "existing";
                const checked = selected.includes(item.id);
                return (
                  <label
                    key={item.id}
                    className={`block border p-3 ${exists ? "border-warm-border bg-sand/20" : "border-pine/25 bg-white"}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={exists || checked}
                        disabled={exists || importing}
                        onChange={() => toggle(item.id)}
                        className="mt-1"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-ink">{item.namn}</span>
                          {exists ? (
                            <span className="inline-flex items-center gap-1 text-xs text-pine">
                              <CheckCircle2 size={13} /> finns redan
                            </span>
                          ) : (
                            <span className="text-xs text-falu">saknas</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {TYPE_LABELS[item.typ] || item.typ} · {item.region} · {item.active ? "aktiv" : "inaktiv"} · {MONITORING_LABELS[item.monitoring_mode] || item.monitoring_mode || "bevakning ej angiven"}
                        </div>
                        <div className="text-xs text-bark mt-1">{item.purpose}</div>
                        <div className="text-xs text-muted-foreground mt-1 break-all">{item.url}</div>
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-xs text-muted-foreground">
                {missingItems.length
                  ? `${selected.length} av ${missingItems.length} saknade källor valda.`
                  : "Alla källor i paketet finns redan."}
              </p>
              <button
                type="button"
                data-testid="regional-source-import"
                onClick={importSelected}
                disabled={importing || loading || !selected.length}
                className="inline-flex items-center justify-center gap-2 bg-pine hover:bg-pine-hover disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm px-4 py-2.5"
              >
                {importing ? <Loader2 size={15} className="animate-spin" /> : <Database size={15} />}
                {importing ? "Lägger till…" : "Lägg till valda källor"}
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
